import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DISCIPLINAS_AREA_CV,
  ESTRUCTURAS_AUTORES_CV,
  EXPERIENCIA_VIGENTE_CV,
  FUENTES_FINANCIAMIENTO_CV,
  INSTITUCIONES_CV,
  NOMBRES_RECONOCIMIENTO_CV,
  PAISES_CV,
  ROLES_PROYECTO_CV,
  TIPOS_ANTECEDENTE_ACADEMICO,
  TIPOS_PUBLICACION_CV,
} from '../../../../core/mocks/cv-catalogos.mock';
import {
  anioDeFecha,
  BloqueCv,
  CAMPOS_BLOQUE_CV,
  createEmptyCvData,
  CVData,
  DistincionItem,
  distincionVacia,
  ExperienciaItem,
  experienciaVacia,
  FormacionAcademicaItem,
  formacionAcademicaVacia,
  LineaInvestigacionItem,
  lineaInvestigacionVacia,
  ProyectoItem,
  proyectoVacio,
  PublicacionItem,
  publicacionVacia,
} from '../../../../core/models/cv.models';
import { CvFormulario, CvSectionKey } from '../../../../core/models';
import { DatosPersonales } from '../../../../core/models/perfil.models';
import { CvService } from '../../../../core/services/cv.service';
import { PerfilService } from '../../../../core/services/perfil.service';
import {
  BloqueCurriculumVista,
  construirVistaCurriculum,
  descargarCurriculumPdf,
  descargarCurriculumWord,
  nombreArchivoCurriculum,
} from '../../../../core/utils/cv-curriculum-vista.util';

const STORAGE_BLOQUE_CV = 'anid.mi-perfil.cv-bloque-abierto';

const ETIQUETA_BLOQUE_CV: Record<BloqueCv, string> = {
  estudios: 'Estudios',
  lineas: 'Líneas de investigación',
  experiencia: 'Experiencia laboral',
  proyectos: 'Proyectos',
  publicaciones: 'Publicaciones y Producción',
  reconocimientos: 'Reconocimientos y Premios',
};

const ETIQUETA_BLOQUE_PDI: Record<BloqueCv, string> = {
  estudios: 'Estudio',
  lineas: 'Líneas de Investigación',
  experiencia: 'Experiencia Laboral',
  proyectos: 'Proyectos',
  publicaciones: 'Publicaciones y Producción',
  reconocimientos: 'Reconocimientos y Premios',
};

const SECCION_POR_BLOQUE: Record<BloqueCv, CvSectionKey> = {
  estudios: 'formacionAcademica',
  lineas: 'lineasInvestigacion',
  experiencia: 'experiencia',
  proyectos: 'proyectos',
  publicaciones: 'publicaciones',
  reconocimientos: 'distinciones',
};

@Component({
  selector: 'app-perfil-curriculum-tab',
  imports: [CommonModule, FormsModule],
  templateUrl: './perfil-curriculum-tab.html',
  styleUrl: './perfil-curriculum-tab.css',
})
export class PerfilCurriculumTab implements OnInit, OnDestroy {
  readonly tiposAntecedente = TIPOS_ANTECEDENTE_ACADEMICO;
  readonly instituciones = INSTITUCIONES_CV;
  readonly paises = PAISES_CV;
  readonly disciplinas = DISCIPLINAS_AREA_CV;
  readonly experienciaVigente = EXPERIENCIA_VIGENTE_CV;
  readonly fuentesFinanciamiento = FUENTES_FINANCIAMIENTO_CV;
  readonly rolesProyecto = ROLES_PROYECTO_CV;
  readonly tiposPublicacion = TIPOS_PUBLICACION_CV;
  readonly estructurasAutores = ESTRUCTURAS_AUTORES_CV;
  readonly nombresReconocimiento = NOMBRES_RECONOCIMIENTO_CV;

  cvData: CVData = createEmptyCvData();
  mensajeCv = '';
  importacionPendiente: CVData | null = null;
  bloqueAbierto: BloqueCv | null = 'estudios';
  edicionCv: Record<BloqueCv, number | null> = this.edicionVacia();
  formularioCv: CvFormulario = this.formularioVacio();
  modalCurriculumVisible = false;
  menuDescargaVisible = false;
  vistaCurriculum: BloqueCurriculumVista[] = [];
  private datosPersonales: DatosPersonales | null = null;

  constructor(
    private readonly cvService: CvService,
    private readonly perfilService: PerfilService
  ) {}

  ngOnInit(): void {
    this.cvService.getCv().subscribe((data) => {
      this.cvData = data;
      this.bloqueAbierto = this.resolverBloqueInicial(data.ultimoBloque);
    });
  }

  ngOnDestroy(): void {
    this.liberarScrollCuerpo();
  }

  @HostListener('document:keydown.escape')
  cerrarConEscape(): void {
    if (this.menuDescargaVisible) {
      this.menuDescargaVisible = false;
      return;
    }
    if (this.modalCurriculumVisible) {
      this.cerrarModalCurriculum();
    }
  }

  abrirModalCurriculum(): void {
    this.perfilService.getDatosPersonales().subscribe((perfil) => {
      this.datosPersonales = perfil;
      this.vistaCurriculum = construirVistaCurriculum(perfil, this.cvData);
      this.modalCurriculumVisible = true;
      this.menuDescargaVisible = false;
      document.body.style.overflow = 'hidden';
    });
  }

  cerrarModalCurriculum(): void {
    this.modalCurriculumVisible = false;
    this.menuDescargaVisible = false;
    this.liberarScrollCuerpo();
  }

  alternarMenuDescarga(event?: Event): void {
    event?.stopPropagation();
    this.menuDescargaVisible = !this.menuDescargaVisible;
  }

  descargarCurriculum(formato: 'pdf' | 'word'): void {
    const archivo =
      formato === 'pdf'
        ? nombreArchivoCurriculum(this.datosPersonales, 'pdf')
        : nombreArchivoCurriculum(this.datosPersonales, 'doc');
    if (formato === 'pdf') {
      descargarCurriculumPdf(this.vistaCurriculum, archivo, this.datosPersonales);
    } else {
      descargarCurriculumWord(this.vistaCurriculum, archivo, this.datosPersonales);
    }
    this.menuDescargaVisible = false;
  }

  estaAbierto(bloque: BloqueCv): boolean {
    return this.bloqueAbierto === bloque;
  }

  alternar(bloque: BloqueCv): void {
    this.bloqueAbierto = this.bloqueAbierto === bloque ? null : bloque;
    if (this.bloqueAbierto) {
      this.persistirBloqueLocal(this.bloqueAbierto);
    }
  }

  catalogoConValor(catalogo: string[], valor: string): string[] {
    if (valor && !catalogo.includes(valor)) {
      return [...catalogo, valor];
    }
    return catalogo;
  }

  prepararImportacionPdi(): void {
    this.cvService.autollenarDesdePortal().subscribe((data) => {
      this.importacionPendiente = data;
      const bloques = this.bloquesConDatos(data);
      this.mensajeCv = bloques.length ? '' : 'PDI no devolvió bloques para precargar.';
    });
  }

  confirmarImportacionPdi(): void {
    if (!this.importacionPendiente) return;
    const payload = this.payloadDesdePdi(this.importacionPendiente);
    const bloques = this.bloquesConDatos(this.importacionPendiente);
    if (!bloques.length) {
      this.importacionPendiente = null;
      this.mensajeCv = 'No había bloques con datos para precargar.';
      return;
    }

    payload.ultimoBloque = bloques[0];
    this.cvService.guardarCv(payload).subscribe((data) => {
      this.cvData = {
        ...data,
        formacionAcademica: [...data.formacionAcademica],
        lineasInvestigacion: [...data.lineasInvestigacion],
        experiencia: [...data.experiencia],
        publicaciones: [...data.publicaciones],
        proyectos: [...data.proyectos],
        distinciones: [...data.distinciones],
      };
      this.importacionPendiente = null;
      this.formularioCv = this.formularioVacio();
      this.edicionCv = this.edicionVacia();
      this.bloqueAbierto = bloques[0];
      this.persistirBloqueLocal(bloques[0]);
      this.mensajeCv = `Currículum de Camilo Andrés García precargado: ${this.textoBloquesPdi(bloques)}.`;
    });
  }

  cancelarImportacionPdi(): void {
    this.importacionPendiente = null;
    this.mensajeCv = 'Importación cancelada. El currículum no se modificó.';
  }

  get textoVistaPreviaPdi(): string {
    if (!this.importacionPendiente) return '';
    const partes = this.bloquesConDatos(this.importacionPendiente).map(
      (bloque) => `${ETIQUETA_BLOQUE_PDI[bloque]} (${this.cantidadBloque(this.importacionPendiente as CVData, bloque)})`
    );
    if (!partes.length) {
      return 'No se detectaron bloques con datos.';
    }
    const listado = partes.length === 1 ? partes[0] : `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
    return `Se detectaron datos para importar: ${listado}. Al confirmar se precargarán solo estos bloques.`;
  }

  agregarRegistro(bloque: BloqueCv): void {
    const seccion = SECCION_POR_BLOQUE[bloque];
    const formulario = this.normalizarFechas(bloque, { ...this.formularioCv[seccion] } as never);
    if (this.formularioVacioDe(formulario)) {
      this.mensajeCv = 'Escribe al menos un dato para agregar el registro. Los asteriscos no bloquean el guardado.';
      return;
    }

    const lista = [...(this.cvData[seccion] as unknown as object[])];
    const indexEdicion = this.edicionCv[bloque];
    const esAlta = indexEdicion === null;
    if (indexEdicion === null) {
      lista.push(formulario);
    } else {
      lista[indexEdicion] = formulario;
    }

    this.cvData = { ...this.cvData, [seccion]: lista } as CVData;
    this.formularioCv[seccion] = this.vacioDeSeccion(seccion) as never;
    this.edicionCv[bloque] = null;
    this.persistirBloque(bloque, esAlta ? 'Se agregó correctamente.' : 'Guardado exitoso.');
  }

  editarRegistro(bloque: BloqueCv, index?: number): void {
    const seccion = SECCION_POR_BLOQUE[bloque];
    const lista = this.cvData[seccion] as unknown as object[];
    const destino = index ?? this.edicionCv[bloque] ?? (lista.length ? lista.length - 1 : null);
    if (destino === null) {
      this.mensajeCv = '';
      return;
    }

    this.formularioCv[seccion] = { ...(lista[destino] as object) } as never;
    this.edicionCv[bloque] = destino;
    this.bloqueAbierto = bloque;
    this.mensajeCv = '';
  }

  eliminarRegistro(bloque: BloqueCv, index?: number): void {
    const seccion = SECCION_POR_BLOQUE[bloque];
    const lista = [...(this.cvData[seccion] as unknown as object[])];
    const destino = index ?? this.edicionCv[bloque] ?? (lista.length ? lista.length - 1 : null);
    if (destino === null) {
      return;
    }

    lista.splice(destino, 1);
    this.cvData = { ...this.cvData, [seccion]: lista } as CVData;
    this.formularioCv[seccion] = this.vacioDeSeccion(seccion) as never;
    this.edicionCv[bloque] = null;
    this.persistirBloque(bloque, 'Registro eliminado.');
  }

  etiquetaAgregar(bloque: BloqueCv): string {
    if (this.edicionCv[bloque] !== null) {
      return this.etiquetaGuardar(bloque);
    }
    if (bloque === 'estudios') return 'Agregar Estudio';
    if (bloque === 'lineas') return 'Agregar Línea';
    if (bloque === 'experiencia') return 'Agregar Experiencia';
    if (bloque === 'proyectos') return 'Agregar Proyecto';
    if (bloque === 'publicaciones') return 'Agregar Publicación';
    return 'Agregar Reconocimiento';
  }

  resumenEstudio(item: FormacionAcademicaItem): string {
    const titulo = item.grado || item.tipoAntecedente || 'Estudio';
    const periodo = [anioDeFecha(item.fechaInicio) || item.anioInicio, anioDeFecha(item.fechaTermino) || item.anioTermino]
      .filter(Boolean)
      .join(' / ');
    return `${titulo}${item.institucion ? ` - ${item.institucion}` : ''}${periodo ? ` (${periodo})` : ''}`;
  }

  resumenLinea(item: LineaInvestigacionItem): string {
    return [item.linea, item.disciplinaArea].filter(Boolean).join(' — ') || 'Línea de investigación';
  }

  resumenExperiencia(item: ExperienciaItem): string {
    return `${item.cargo || 'Experiencia'}${item.institucion ? ` - ${item.institucion}` : ''}`;
  }

  resumenProyecto(item: ProyectoItem): string {
    return `${item.nombreProyecto || 'Proyecto'}${item.rol ? ` - ${item.rol}` : ''}`;
  }

  resumenPublicacion(item: PublicacionItem): string {
    return `${item.tipo || 'Publicación'}${item.titulo ? ` - ${item.titulo}` : ''}`;
  }

  resumenReconocimiento(item: DistincionItem): string {
    return `${item.nombreReconocimiento || 'Reconocimiento'}${item.institucionOtorga ? ` - ${item.institucionOtorga}` : ''}`;
  }

  private bloquesConDatos(data: CVData): BloqueCv[] {
    return (Object.keys(CAMPOS_BLOQUE_CV) as BloqueCv[]).filter((bloque) => this.cantidadBloque(data, bloque) > 0);
  }

  private cantidadBloque(data: CVData, bloque: BloqueCv): number {
    const clave = CAMPOS_BLOQUE_CV[bloque][0];
    const lista = data[clave];
    return Array.isArray(lista) ? lista.length : 0;
  }

  private payloadDesdePdi(data: CVData): Partial<CVData> {
    const payload: Partial<CVData> = {};
    this.bloquesConDatos(data).forEach((bloque) => {
      CAMPOS_BLOQUE_CV[bloque].forEach((clave) => {
        (payload as unknown as Record<string, unknown>)[clave] = JSON.parse(JSON.stringify(data[clave]));
      });
    });
    return payload;
  }

  private textoBloquesPdi(bloques: BloqueCv[]): string {
    return bloques.map((bloque) => ETIQUETA_BLOQUE_CV[bloque]).join(', ');
  }

  private persistirBloque(bloque: BloqueCv, mensajeExito: string): void {
    const payload: Partial<CVData> = { ultimoBloque: bloque };
    CAMPOS_BLOQUE_CV[bloque].forEach((clave) => {
      (payload as Record<string, unknown>)[clave] = this.cvData[clave];
    });

    this.cvService.guardarCv(payload).subscribe((data) => {
      this.cvData = data;
      this.bloqueAbierto = bloque;
      this.persistirBloqueLocal(bloque);
      this.mensajeCv = mensajeExito;
    });
  }

  private normalizarFechas(bloque: BloqueCv, item: object): object {
    if (bloque === 'estudios') {
      const estudio = item as FormacionAcademicaItem;
      estudio.anioInicio = anioDeFecha(estudio.fechaInicio) || estudio.anioInicio;
      estudio.anioTermino = anioDeFecha(estudio.fechaTermino) || estudio.anioTermino;
      return estudio;
    }
    if (bloque === 'proyectos') {
      const proyecto = item as ProyectoItem;
      proyecto.anioInicio = anioDeFecha(proyecto.fechaInicio) || proyecto.anioInicio;
      proyecto.anioTermino = anioDeFecha(proyecto.fechaTermino) || proyecto.anioTermino;
      return proyecto;
    }
    if (bloque === 'publicaciones') {
      const publicacion = item as PublicacionItem;
      publicacion.anio = anioDeFecha(publicacion.fecha) || publicacion.anio;
      return publicacion;
    }
    if (bloque === 'reconocimientos') {
      const distincion = item as DistincionItem;
      distincion.anio = anioDeFecha(distincion.fecha) || distincion.anio;
      return distincion;
    }
    return item;
  }

  private formularioVacioDe(item: object): boolean {
    return Object.values(item as Record<string, unknown>).every((valor) => {
      if (typeof valor === 'string') return valor.trim().length === 0;
      return valor === null || valor === undefined;
    });
  }

  private vacioDeSeccion(seccion: CvSectionKey) {
    if (seccion === 'formacionAcademica') return formacionAcademicaVacia();
    if (seccion === 'lineasInvestigacion') return lineaInvestigacionVacia();
    if (seccion === 'experiencia') return experienciaVacia();
    if (seccion === 'publicaciones') return publicacionVacia();
    if (seccion === 'proyectos') return proyectoVacio();
    if (seccion === 'distinciones') return distincionVacia();
    if (seccion === 'docencia') return { curso: '', institucion: '', periodo: '' };
    return { idioma: '', nivel: '' };
  }

  private formularioVacio(): CvFormulario {
    return {
      formacionAcademica: formacionAcademicaVacia(),
      lineasInvestigacion: lineaInvestigacionVacia(),
      experiencia: experienciaVacia(),
      publicaciones: publicacionVacia(),
      proyectos: proyectoVacio(),
      distinciones: distincionVacia(),
      docencia: { curso: '', institucion: '', periodo: '' },
      idiomas: { idioma: '', nivel: '' },
    };
  }

  private edicionVacia(): Record<BloqueCv, number | null> {
    return {
      estudios: null,
      lineas: null,
      experiencia: null,
      proyectos: null,
      publicaciones: null,
      reconocimientos: null,
    };
  }

  private etiquetaGuardar(bloque: BloqueCv): string {
    if (bloque === 'estudios') return 'Guardar Estudio';
    if (bloque === 'lineas') return 'Guardar Línea';
    if (bloque === 'experiencia') return 'Guardar Experiencia';
    if (bloque === 'proyectos') return 'Guardar Proyecto';
    if (bloque === 'publicaciones') return 'Guardar Publicación';
    return 'Guardar Reconocimiento';
  }

  private resolverBloqueInicial(ultimo: BloqueCv | ''): BloqueCv {
    const guardado = localStorage.getItem(STORAGE_BLOQUE_CV) as BloqueCv | null;
    if (this.esBloqueCv(ultimo)) return ultimo;
    if (this.esBloqueCv(guardado)) return guardado;
    return 'estudios';
  }

  private esBloqueCv(valor: string | null): valor is BloqueCv {
    return (
      valor === 'estudios' ||
      valor === 'lineas' ||
      valor === 'experiencia' ||
      valor === 'proyectos' ||
      valor === 'publicaciones' ||
      valor === 'reconocimientos'
    );
  }

  private persistirBloqueLocal(bloque: BloqueCv): void {
    localStorage.setItem(STORAGE_BLOQUE_CV, bloque);
  }

  private liberarScrollCuerpo(): void {
    document.body.style.overflow = '';
  }
}
